import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as userDeviceHistoryApi from '../../api/userDeviceHistory';
import { message } from 'antd';

// 获取用户设备历史列表
export const fetchUserDeviceHistories = createAsyncThunk(
  'userDeviceHistory/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userDeviceHistoryApi.getUserDeviceHistoryList(params);
      console.log("response",response.data);
      if (response && response.code === 200) {
        // 返回数据和查询参数，以便在reducer中保存
        return {
          data: response.data,
          params
        };
      }
      return rejectWithValue(response?.message || '获取用户设备历史列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户设备历史列表失败');
    }
  }
);

// 获取用户常用设备列表
export const fetchMostFrequentDevices = createAsyncThunk(
  'userDeviceHistory/fetchFrequentDevices',
  async ({ userId, limit = 5 }, { rejectWithValue }) => {
    try {
      const response = await userDeviceHistoryApi.getMostFrequentDevices(userId, limit);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取用户常用设备列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户常用设备列表失败');
    }
  }
);

// 检查设备是否为用户的常用设备
export const checkFrequentDevice = createAsyncThunk(
  'userDeviceHistory/checkFrequentDevice',
  async ({ userId, deviceId }, { rejectWithValue }) => {
    try {
      const response = await userDeviceHistoryApi.isFrequentDevice(userId, deviceId);
      if (response && response.code === 200) {
        return {
          userId,
          deviceId,
          isFrequent: response.data
        };
      }
      return rejectWithValue(response?.message || '检查常用设备失败');
    } catch (error) {
      return rejectWithValue(error.message || '检查常用设备失败');
    }
  }
);

// 初始状态
const initialState = {
  // 用户设备历史列表
  deviceHistoryList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      userId: undefined,
      deviceId: undefined,
      deviceType: undefined,
      isOnline: undefined
    }
  },
  // 用户常用设备列表
  frequentDevices: {
    data: [],
    loading: false,
    error: null
  },
  // 设备频率检查
  deviceFrequencyCheck: {
    results: {},  // 格式: { userId_deviceId: boolean }
    loading: false,
    error: null
  }
};

// 创建Slice
const userDeviceHistorySlice = createSlice({
  name: 'userDeviceHistory',
  initialState,
  reducers: {
    setSearchParams: (state, action) => {
      state.deviceHistoryList.searchParams = {
        ...state.deviceHistoryList.searchParams,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取用户设备历史列表
      .addCase(fetchUserDeviceHistories.pending, (state) => {
        state.deviceHistoryList.loading = true;
        state.deviceHistoryList.error = null;
      })
      .addCase(fetchUserDeviceHistories.fulfilled, (state, action) => {
        state.deviceHistoryList.loading = false;
        state.deviceHistoryList.data = action.payload.data.records || [];
        state.deviceHistoryList.total = action.payload.data.total || 0;
        state.deviceHistoryList.current = action.payload.data.current || 1;
        state.deviceHistoryList.size = action.payload.data.size || 10;
        
        // 保存搜索参数
        if (action.payload.params) {
          state.deviceHistoryList.searchParams = {
            ...state.deviceHistoryList.searchParams,
            ...action.payload.params
          };
        }
      })
      .addCase(fetchUserDeviceHistories.rejected, (state, action) => {
        state.deviceHistoryList.loading = false;
        state.deviceHistoryList.error = action.payload;
        message.error(action.payload || '获取用户设备历史列表失败');
      })
      
      // 处理获取用户常用设备列表
      .addCase(fetchMostFrequentDevices.pending, (state) => {
        state.frequentDevices.loading = true;
        state.frequentDevices.error = null;
      })
      .addCase(fetchMostFrequentDevices.fulfilled, (state, action) => {
        state.frequentDevices.loading = false;
        state.frequentDevices.data = action.payload || [];
      })
      .addCase(fetchMostFrequentDevices.rejected, (state, action) => {
        state.frequentDevices.loading = false;
        state.frequentDevices.error = action.payload;
        message.error(action.payload || '获取用户常用设备列表失败');
      })
      
      // 处理检查设备是否为用户的常用设备
      .addCase(checkFrequentDevice.pending, (state) => {
        state.deviceFrequencyCheck.loading = true;
        state.deviceFrequencyCheck.error = null;
      })
      .addCase(checkFrequentDevice.fulfilled, (state, action) => {
        state.deviceFrequencyCheck.loading = false;
        const { userId, deviceId, isFrequent } = action.payload;
        state.deviceFrequencyCheck.results[`${userId}_${deviceId}`] = isFrequent;
      })
      .addCase(checkFrequentDevice.rejected, (state, action) => {
        state.deviceFrequencyCheck.loading = false;
        state.deviceFrequencyCheck.error = action.payload;
        message.error(action.payload || '检查常用设备失败');
      });
  }
});

export const { setSearchParams } = userDeviceHistorySlice.actions;

export default userDeviceHistorySlice.reducer; 